import {useState} from "react";
import {useDispatch, useSelector} from "react-redux";

import {ALL_SOURCES} from "@reva/shared/parameter-resolution";

import {useSelectByCurrentLanguage} from "../../hooks/languageHooks";
import {selectPersistenceErrorForSingleParam} from "../../redux/persistenceSelectors";
import {removePersistenceSingleParamError, selectReadOnly} from "../../redux/persistenceSlice";
import {resetPersistenceToLastValidResponse} from "../../redux/persistenceThunk";
import CommentIcon from "./CommentIcon";
import {commentConfirmValue, hasOwnLayerEmptyValue} from "./commentConfirmValue";
import LabelWithTooltip from "./LabelWithTooltip";
import {
    getResourceColor,
    useInputParameterDef,
    useLocalizedFieldValidationMessage,
    useParameterValue,
} from "./paramHooks";
import ResetIcon from "./ResetIcon";
import SetEmptyIcon from "./SetEmptyIcon";
import ValuesIcon from "./ValuesIcon";

/**
 * @callback ValueChangeHandler
 * @param {*} e change event
 */

/**
 * @typedef {object} RenderControlParams
 * @property {string} className
 * @property {ValueChangeHandler} onChange
 * @property {*} value
 * @property {ResolvedParameterSource|null} activeParameterType
 * @property {boolean} hasConflict
 * @property {boolean} hasError
 * @property {boolean} disabled
 * @property {string} label
 * @property {ObjectParameterValues} allValues
 */

/**
 * @callback RenderInputControlFunction
 * @param {Partial<RenderControlParams>} controlParams calculated parameters/state of control
 * @return {null|React.ReactNode} rendered control
 */

/**
 * @callback ResetOrEmptyFunction
 * @param {"empty"|null} value value to set parameter to
 * @return {void}
 */

/**
 * Container and label for a field.
 * @param {object} props
 * @param {"default"|"checkbox"} [props.layout="default"] layout of the field (default or checkbox)
 * @param {string} props.id htmlId to connect to the label
 * @param {string|null} [props.label] rendered label (override from parameter details)
 * @param {boolean|null} [props.required] flag, if the field should be rendered as required (override from parameter details)
 * @param {string|null} [props.parameter] optional parameter name, to get label and required from
 * @param {RevaValueParameterType} props.parameterType optional parameter name, to get label and required from
 * @param {InputParameterDetailsDef|null} [props.parameter_details_def] optional parameter details definition, to use instead of parameter
 * @param {boolean|null} [props.showUnit] optional flag to wrap the input in an input group, if the parameter metadata has a unit
 * @param {boolean|null} [props.showDescription] optional flag to display an info icon with the description as tooltip
 * @param {boolean|null} [props.noReset] optional flag to not display the reset icon for not editable parameters
 * @param {ResetOrEmptyFunction|null} [props.onResetOrEmpty] optional custom function to set parameter to null or empty
 * @param {boolean|null} [props.hasAssociatedOutputParam] flag, if the parameter has an associated output parameter
 * @param {string|number|null} [props.associatedOutputParamValue] associated output parameter value, if available
 * @param {boolean} [props.showAssociatedOutputParamHint=true] whether the label tooltip explains the ƒ()-prefixed fallback value
 * @param {string|null} [props.extraTooltipText] optional extra text appended to the label tooltip, e.g. to explain a field-specific default
 * @param {boolean|null} [props.showLabel] optional flag, if the label of the input should be rendered
 * @param {boolean|null} [props.noInputGroup] optional flag, to render no input group CSS class in container wrapper
 * @param {RenderInputControlFunction} props.renderControl function to render the control
 * @param {function} [props.eventToValueMapper] function to map the event to a value
 * @param {RenderInputControlFunction} [props.renderAdditionalAction] optional additional action next to the control
 * @return {React.ReactNode}
 */
export default function ParameterFieldContainer({
    layout = "default",
    id,
    label,
    required,
    parameter,
    parameterType,
    parameter_details_def,
    showUnit = false,
    showDescription = false,
    noReset = false,
    onResetOrEmpty,
    hasAssociatedOutputParam = false,
    associatedOutputParamValue = null,
    showAssociatedOutputParamHint = true,
    extraTooltipText,
    showLabel = true,
    noInputGroup = false,
    renderControl,
    eventToValueMapper,
    renderAdditionalAction,
}) {
    const {parameterValues, onValueChange} = useParameterValue(parameter, parameterType);
    const {activeParameterValue, hasConflict, activeParameterType, allValues, comment} = parameterValues;

    const parameterData = useInputParameterDef(parameter, parameter_details_def);
    const selectByCurrentLanguage = useSelectByCurrentLanguage();
    const dispatch = useDispatch();

    const maybeErrorMessage = useLocalizedFieldValidationMessage(parameter, parameterType, hasConflict);
    const disabled = useSelector(selectReadOnly);

    const [commentOpen, setCommentOpen] = useState(false);

    const persistenceError = useSelector(selectPersistenceErrorForSingleParam(parameter));
    if (!parameterType) {
        console.warn(`ParameterFieldContainer: parameterType is undefined for parameter: ${parameter}`);
        return null;
    }

    if (parameter && !parameterData && parameter !== "segment") return null;

    const effectiveRequired = typeof required === "boolean" || !parameterData ? !!required : parameterData.required;

    const effectiveLabel =
        typeof label === "string" || !parameterData
            ? label
            : selectByCurrentLanguage(parameterData.nameDe, parameterData.nameEn);
    const unit = showUnit && parameterData ? selectByCurrentLanguage(parameterData.unitDe, parameterData.unitEn) : null;

    const hasError = !!maybeErrorMessage || hasConflict;
    // #17901 benötigte Parameter Eingaben dürfen nicht explizit auf leer (e.g. empty) gesetzt werden
    // #18195 "Keine Angabe" soll nicht mehr über den Input selber gesetzt werden können
    const defaultEventToValueMapper = (e) => ({value: e.target.value ?? null});
    const handleChange = (e) => {
        const mappedValue = eventToValueMapper ? eventToValueMapper(e) : defaultEventToValueMapper(e);
        const value = mappedValue.value;
        if (value === "empty" && activeParameterValue == null) {
            return;
        }
        onValueChange(value, comment);
    };

    const hasResetIcon =
        !noReset && !disabled && isMatchingTab(activeParameterType, parameterType, activeParameterValue);
    const resetIcon = hasResetIcon ? (
        <ResetIcon onValueChange={onResetOrEmpty ?? onValueChange} parameter={parameter} />
    ) : null;
    const commentRequired = !!persistenceError;
    // #19774: no ✕ once the field already says "Keine Angabe" — a second click only resent it without its comment
    const hasSetEmptyIcon =
        !disabled &&
        !effectiveRequired &&
        parameter !== "segment" &&
        parameterType !== "DOC_PROPERTY" &&
        !hasOwnLayerEmptyValue(parameterType, allValues);
    const setEmptyIcon = hasSetEmptyIcon ? (
        <SetEmptyIcon onValueChange={onResetOrEmpty ?? onValueChange} empty={true} />
    ) : null;

    function isMatchingTab(activeType, tabType, value) {
        // DOC_PROPERTY (z.B. location_flood_risk) hat kein Gegenstück in ALL_SOURCES, resolveActiveParameterValue
        // liefert dafür nie ein activeType - daher hier direkt am gesetzten Wert erkennen (#19115).
        if (tabType === "DOC_PROPERTY") {
            return value != null;
        }
        switch (activeType) {
            case "reva":
                return tabType === "AVM";
            case "register":
                return tabType === "REGISTER" || typeof onResetOrEmpty === "function";
            case "energy":
                return tabType === "ENERGY_CERT";
            case "plot":
                return tabType === "PLOT";
            case "inspectionDerived":
                return tabType === "INSPECTION_DERIVED" || typeof onResetOrEmpty === "function";
            default:
                return false;
        }
    }

    function isActiveValueRelevantChange(parameterType, activeParameterValue) {
        // loop through ALL_SOURCES and check if the first non-null value is the same as activeParameterValue
        for (const source of ALL_SOURCES) {
            const value = allValues[source];
            // die aktuelle Schicht wird nicht berücksichtigt
            const isDifferentSource = source?.toLowerCase() !== parameterType?.toLowerCase();
            if (value != null && value !== "-" && value !== "empty" && isDifferentSource) {
                return value !== activeParameterValue;
            }
        }
        return true;
    }

    function cancelComment() {
        if (!persistenceError) {
            return;
        }
        // Nach einer Ablehnung (R500) steht serverseitig noch der zuletzt gespeicherte Stand, nur der lokal schon
        // gesetzte Wert muss weg — deshalb rein lokal auf diesen Stand zurücksetzen statt zu schreiben (#19444).
        // Ein Schreiben von null hätte einen bestehenden Override samt Kommentar gelöscht (#19774).
        // Den Fehler dabei selbst abräumen: Ohne Schreibvorgang tut es sonst niemand, und solange
        // er steht, hält er diesen Dialog offen und sperrt zugleich Escape und Maske.
        dispatch(resetPersistenceToLastValidResponse());
        dispatch(removePersistenceSingleParamError());
    }

    const commentIcon = (
        <CommentIcon
            parameterType={parameterType}
            open={commentOpen || !!persistenceError}
            required={commentRequired}
            onOpen={() => {
                setCommentOpen(true);
            }}
            onClose={() => {
                setCommentOpen(false);
            }}
            onConfirm={(text) => {
                onValueChange(commentConfirmValue(parameterType, allValues, activeParameterValue), text);
            }}
            onReset={cancelComment}
            onClear={
                comment && !required && allValues[parameterType?.toLowerCase()] !== "empty"
                    ? () => {
                          // Falls der activeParameterValue keine relevante Änderung darstellt, weil eine unterliegende Schicht denselben wert hat -> null persistieren
                          const toPersist = isActiveValueRelevantChange(parameterType, activeParameterValue)
                              ? activeParameterValue
                              : null;
                          onValueChange(toPersist, null);
                      }
                    : null
            }
            comment={comment}
            label={effectiveLabel}
        />
    );
    const nonEmptyValues = ALL_SOURCES.map((key) => [key, allValues?.[key]]).filter(
        ([key, value]) => value != null && value !== "-",
    );
    // Im Gutachten kann nur diese Quelle verwendet werden oder keine wenn ein Konflikt besteht
    // selbst im Falle eines Konflikts wollen wir diesen Wert ausweisen!
    const valuationValue = nonEmptyValues.length === 0 ? "-" : nonEmptyValues[0][1];
    const valuesIcon = (
        <ValuesIcon
            parameterType={parameterType}
            parameter_details_def={parameterData}
            allValues={allValues ? {...allValues, valuationValue} : {parameter, valuationValue}}
            tooltipRef={parameter}
            label={effectiveLabel}
            associatedOutputParamValue={hasAssociatedOutputParam ? associatedOutputParamValue : null}
        />
    );

    const errorComponent = hasError && (
        <small className={`p-error block${layout === "checkbox" ? " mt-0 ml-1 " : " mt-2 "}${parameter}`}>
            {maybeErrorMessage}
        </small>
    );

    /** @type {RenderControlParams} */
    const controlParams = {
        className: hasError || hasConflict ? "p-invalid" : "",
        onChange: handleChange,
        value: activeParameterValue,
        activeParameterType,
        hasConflict,
        hasError,
        disabled,
        label: effectiveLabel,
        allValues,
    };
    const control = renderControl(controlParams);
    const additionalAction = renderAdditionalAction ? renderAdditionalAction(controlParams) : null;

    // Render checkbox layout
    if (layout === "checkbox") {
        const border = getResourceColor(activeParameterType, hasConflict);

        const labelElement = showLabel ? (
            <LabelWithTooltip
                id={id}
                label={effectiveLabel}
                parameterData={parameterData}
                required={effectiveRequired && parameterType === "AVM"}
                hasError={hasError}
                className="ml-4"
                hasAssociatedOutputParam={hasAssociatedOutputParam && showAssociatedOutputParamHint}
                extraTooltipText={extraTooltipText}
                value={activeParameterValue}
            />
        ) : null;

        return (
            <div
                key={id}
                className={`field p-inputtext-sm text-sm flex items-center ${
                    hasConflict ? `p-2 border rounded-border border-${border}` : ""
                }`}
            >
                {control}
                <div className="flex-1 flex items-center">
                    {labelElement}
                    {valuesIcon}
                    {commentIcon}
                    {resetIcon}
                    {setEmptyIcon}
                </div>
                {errorComponent}
            </div>
        );
    }

    const controlWrapper = (
        <div className="w-full">
            <div className={`w-full${noInputGroup ? "" : " p-inputgroup"}`}>
                {control}
                {unit && <span className="p-inputgroup-addon xl:shrink-0">{unit}</span>}
                {additionalAction}
            </div>
            {errorComponent}
        </div>
    );

    const labelElement = showLabel ? (
        <LabelWithTooltip
            id={id}
            label={effectiveLabel}
            parameterData={parameterData}
            required={effectiveRequired && parameterType === "AVM"}
            hasError={hasError}
            hasAssociatedOutputParam={hasAssociatedOutputParam && showAssociatedOutputParamHint}
            extraTooltipText={extraTooltipText}
            value={activeParameterValue}
        />
    ) : null;

    return (
        <div key={id} className="field p-inputtext-sm text-sm">
            {labelElement}
            {valuesIcon}
            {commentIcon}
            {resetIcon}
            {setEmptyIcon}
            {showDescription ? (
                <div className="flex-1 flex flex-col items-center">{controlWrapper}</div>
            ) : (
                controlWrapper
            )}
        </div>
    );
}
